import type { Canal, Configuracion, ConfiguracionEscrita, EstadoAsistente, Herramienta, Pregunta, Tono, Tramo } from '../../api/ops/asistente';
import type { Contador } from '../../api/operaciones';
import type { EstadoChip } from '../../componentes/Chip';
import type { NombreIcono } from '../../componentes/iconos';

/**
 * Reglas de la pantalla «Asistente» (11.5 a 11.7), sin React, con los textos de la exportación de
 * `diseno/canales-asistente/` (`11.5-11.7-asistente.html`). Lo que la API no da no se inventa aquí.
 */

/** Los apartados de la barra (una sola barra, `?apartado=`): Configuración · Preguntas · Instrucciones · Sin asistente · Probar y publicar. */
export const APARTADOS = [
    { valor: 'configuracion', etiqueta: 'Configuración', corta: 'Configurar', icono: 'sliders-horizontal' },
    { valor: 'preguntas', etiqueta: 'Preguntas', corta: 'Preguntas', icono: 'message-square-text' },
    { valor: 'instrucciones', etiqueta: 'Instrucciones', corta: 'Info', icono: 'file-text' },
    { valor: 'sin-asistente', etiqueta: 'Sin asistente', corta: 'Sin asistente', icono: 'user-x' },
    { valor: 'probar', etiqueta: 'Probar y publicar', corta: 'Publicar', icono: 'rocket' },
] as const satisfies readonly { valor: string; etiqueta: string; corta: string; icono: NombreIcono }[];

export type Apartado = (typeof APARTADOS)[number]['valor'];

export function apartadoDe(pedido: string | null): Apartado {
    return APARTADOS.find((a) => a.valor === pedido)?.valor ?? 'configuracion';
}

export const TONOS: readonly { valor: Tono; etiqueta: string; texto: string; ejemplo: string }[] = [
    { valor: 'friendly', etiqueta: 'Cercano', texto: 'De tú, amable y con alguna exclamación.', ejemplo: '¡Hola! Sí, el sábado nos queda sitio para 4 a las 22:30. ¿Te lo guardo?' },
    { valor: 'formal', etiqueta: 'Formal', texto: 'De usted, correcto y sin bromas.', ejemplo: 'Buenas tardes. Disponemos de mesa para cuatro el sábado a las 22:30. ¿Desea que la reservemos?' },
    { valor: 'concise', etiqueta: 'Directo', texto: 'Frases cortas, al grano.', ejemplo: 'Sábado, 4 personas: hay sitio a las 22:30. ¿Reservo?' },
];

export const IDIOMAS: readonly { valor: string; etiqueta: string }[] = [
    { valor: 'es-ES', etiqueta: 'Español' },
    { valor: 'en-GB', etiqueta: 'Inglés' },
    { valor: 'fr-FR', etiqueta: 'Francés' },
    { valor: 'de-DE', etiqueta: 'Alemán' },
    { valor: 'it-IT', etiqueta: 'Italiano' },
    { valor: 'pt-PT', etiqueta: 'Portugués' },
    { valor: 'ca-ES', etiqueta: 'Catalán' },
];

export const DIAS = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'] as const;

export const nombreTono = (t: Tono) => TONOS.find((x) => x.valor === t)?.etiqueta ?? t;
export const nombreIdioma = (l: string) => IDIOMAS.find((x) => x.valor === l)?.etiqueta ?? l;

/** «a, b y c». */
export function enLista(partes: readonly string[]): string {
    return partes.length <= 1 ? (partes[0] ?? '') : `${partes.slice(0, -1).join(', ')} y ${partes[partes.length - 1] ?? ''}`;
}

const miles = (n: number) => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/** Lo que se manda a `updateAssistantDraft`: solo los campos de `ConfigWrite` (la petición rechaza los demás, G533). */
export function escritura(c: ConfiguracionEscrita): ConfiguracionEscrita {
    return {
        assistant_name: c.assistant_name,
        tone: c.tone,
        locales: c.locales,
        channel_ids: c.channel_ids ?? [],
        human_hours: c.human_hours ?? [],
        instructions: c.instructions ?? '',
        faqs: (c.faqs ?? []).map((f) => ({ ...(f.locale === undefined ? {} : { locale: f.locale }), question: f.question, answer: f.answer })),
    };
}

// ── Estado del asistente (getAssistant) ──

export function chipDeEstado(estado: EstadoAsistente['status']): { texto: string; estado: EstadoChip } | null {
    switch (estado) {
        case 'not_configured':
            return { texto: 'Sin publicar', estado: 'draft' };
        case 'read_only':
            return { texto: 'Solo lectura', estado: 'problem' };
        case 'off':
            return { texto: 'Apagado', estado: 'neutral' };
        case 'quota_exhausted':
            return { texto: 'Cupo agotado', estado: 'problem' };
        case 'season_pause':
            return { texto: 'En pausa', estado: 'pending' };
        case 'no_entitlement':
            return null;
        case 'attending':
            return null;
    }
}

export type TonoNota = 'gris' | 'rojo' | 'ambar';

export interface NotaDeEstado {
    tono: TonoNota;
    icono: NombreIcono;
    texto: string;
    /** Botones: «Probar y publicar», «Ir al Buzón» o «Añadir conversaciones» (solo el Propietario). */
    botones: readonly ('probar' | 'buzon' | 'conversaciones')[];
}

/** El aviso bajo la cabecera según el estado y el consumo (G23, G24, G452). */
export function notaDeEstado(estado: EstadoAsistente['status'], uso: Uso | null, propietario: boolean): NotaDeEstado | null {
    switch (estado) {
        case 'not_configured':
            return { tono: 'gris', icono: 'info', texto: 'Tu asistente todavía no atiende. Elige cómo habla, pruébalo y publica la primera versión.', botones: ['probar'] };
        case 'off':
            return { tono: 'gris', icono: 'power', texto: 'Apagado: todos los mensajes van al buzón y los contesta una persona del equipo.', botones: ['buzon'] };
        case 'quota_exhausted':
            return {
                tono: 'rojo',
                icono: 'circle-alert',
                texto:
                    uso === null
                        ? 'Has usado todas las conversaciones de este mes. Las conversaciones nuevas van al buzón; las ya empezadas terminan con el asistente.'
                        : `Has usado las ${miles(uso.tope)} conversaciones de este mes. Las conversaciones nuevas van al buzón hasta el ${uso.renueva}; las ya empezadas terminan con el asistente.`,
                botones: propietario ? ['conversaciones', 'buzon'] : ['buzon'],
            };
        case 'season_pause':
            return { tono: 'ambar', icono: 'pause', texto: 'Pausa de temporada: el asistente no atiende. Quien escribe recibe «Cerrado por temporada» y la conversación va al buzón hasta que reactives el negocio.', botones: ['buzon'] };
        default:
            break;
    }
    if (estado === 'attending' && uso !== null && uso.porcentaje >= 90 && uso.porcentaje < 100) {
        return {
            tono: 'ambar',
            icono: 'triangle-alert',
            texto: `Llevas el ${String(uso.porcentaje)} % de las conversaciones del mes. Al llegar al 100 %, las nuevas irán al buzón.`,
            botones: propietario ? ['conversaciones'] : [],
        };
    }
    return null;
}

export function ayudaDelInterruptor(publicado: boolean, encendido: boolean): string {
    return !publicado ? 'Se puede encender después de publicar la primera versión.' : encendido ? 'Atiende en los canales elegidos con la versión publicada.' : 'Apagado, todos los mensajes van al buzón.';
}

// ── Consumo (getUsage, concepto `conversations`) ──

export interface Uso {
    usadas: number;
    tope: number;
    porcentaje: number;
    /** Día en que se renueva: el siguiente a `period_end` (G335), «1 de noviembre». */
    renueva: string;
    detalle: string;
    nota: string;
}

export function usoDe(contadores: readonly Contador[]): Uso | null {
    const c = contadores.find((x) => x.concept === 'conversations');
    if (c === undefined) {
        return null;
    }
    const tope = c.included + c.extra;
    const fin = new Date(`${c.period_end}T12:00:00`);
    fin.setDate(fin.getDate() + 1);
    const renueva = fin.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' });
    const porcentaje = tope <= 0 ? 0 : Math.min(100, Math.floor((c.used * 100) / tope));
    return {
        usadas: c.used,
        tope,
        porcentaje,
        renueva,
        detalle: `${miles(c.used)} de ${miles(tope)}`,
        nota: porcentaje >= 100 ? `Cupo agotado: las nuevas van al buzón. Se renueva el ${renueva}.` : porcentaje >= 90 ? `Llevas el ${String(porcentaje)} % del mes. Se renueva el ${renueva}.` : `Este mes · se renueva el ${renueva}.`,
    };
}

// ── Configuración (11.5) ──

/** Canales que no atiende el asistente: el email va al buzón (G447) y la voz es otro bloque. */
export function admiteAsistente(c: Canal): boolean {
    return c.channel_type !== 'email' && c.channel_type !== 'voice';
}

export function nombreCanal(c: Canal): string {
    const tipo = { whatsapp: 'WhatsApp', instagram: 'Instagram', telegram: 'Telegram', email: 'Email', web_chat: 'Chat de la web', voice: 'Voz' }[c.channel_type];
    return c.display_name != null && c.display_name !== '' && c.display_name !== tipo ? `${tipo} · ${c.display_name}` : tipo;
}

export function iconoCanal(c: Canal): NombreIcono {
    return ({ whatsapp: 'message-circle', instagram: 'camera', telegram: 'send', email: 'mail', web_chat: 'message-square-text', voice: 'phone' } as const)[c.channel_type];
}

export function resumenIdiomas(locales: readonly string[]): string {
    return `${locales.length === 0 ? 'Ninguno' : enLista(locales.map((l, i) => (i === 0 ? nombreIdioma(l) : nombreIdioma(l).toLocaleLowerCase('es'))))} · responde en el idioma del cliente`;
}

export function resumenCanales(ids: readonly string[], canales: readonly Canal[]): { texto: string; chip: string } {
    const conectados = canales.filter(admiteAsistente);
    if (conectados.length === 0) {
        return { texto: 'Todavía no hay canales conectados', chip: 'Sin canales' };
    }
    if (ids.length === 0) {
        return { texto: 'Todos los conectados, también los nuevos', chip: '' };
    }
    const elegidos = conectados.filter((c) => ids.includes(c.id));
    return elegidos.length === 0 ? { texto: 'En ningún canal: todo va al buzón', chip: 'Apagado' } : { texto: `En ${enLista(elegidos.map(nombreCanal))}`, chip: '' };
}

/** Los tramos de cada día (1 = lunes), en el orden del día. */
export function tramosPorDia(horas: readonly Tramo[]): Tramo[][] {
    return DIAS.map((_, i) => horas.filter((t) => t.weekday === i + 1).sort((a, b) => a.from.localeCompare(b.from)));
}

const textoTramos = (tr: readonly Tramo[]) => tr.map((t) => `${t.from}–${t.to}`).join(' y ');

/** «Martes a sábado»: los días con atención, en tramos seguidos. */
export function resumenDias(horas: readonly Tramo[]): string {
    const dias = tramosPorDia(horas);
    const partes: string[] = [];
    let i = 0;
    while (i < 7) {
        if ((dias[i] ?? []).length === 0) {
            i++;
            continue;
        }
        let j = i;
        while (j + 1 < 7 && (dias[j + 1] ?? []).length > 0) {
            j++;
        }
        partes.push(j > i ? `${DIAS[i] ?? ''} a ${(DIAS[j] ?? '').toLocaleLowerCase('es')}` : (DIAS[i] ?? ''));
        i = j + 1;
    }
    return enLista(partes.map((p, k) => (k === 0 ? p : p.toLocaleLowerCase('es'))));
}

/** «Martes a jueves: 11:00–14:00 y 18:00–22:00 · Sábado: 12:00–22:00». */
export function resumenHoras(horas: readonly Tramo[]): string {
    const dias = tramosPorDia(horas);
    const partes: string[] = [];
    let i = 0;
    while (i < 7) {
        const tr = dias[i] ?? [];
        if (tr.length === 0) {
            i++;
            continue;
        }
        let j = i;
        while (j + 1 < 7 && textoTramos(dias[j + 1] ?? []) === textoTramos(tr)) {
            j++;
        }
        partes.push(`${j > i ? `${DIAS[i] ?? ''} a ${(DIAS[j] ?? '').toLocaleLowerCase('es')}` : (DIAS[i] ?? '')}: ${textoTramos(tr)}`);
        i = j + 1;
    }
    return partes.length === 0 ? 'Sin horario de atención' : partes.join(' · ');
}

export const textoTramosDelDia = textoTramos;

export function resumenAtencion(horas: readonly Tramo[], zona: string): { texto: string; chip: string } {
    if (horas.length === 0) {
        return { texto: 'Sin horario: el asistente no puede decir cuándo respondéis', chip: 'Sin horario' };
    }
    return { texto: `${resumenDias(horas)} · ${zona}`, chip: '' };
}

/** «hora de Madrid» con la zona del negocio (`Europe/Madrid`). */
export function horaDe(zona: string | null | undefined): string {
    if (zona == null || zona === '') {
        return 'hora del negocio';
    }
    const ciudad = zona.split('/').pop()?.replace(/_/g, ' ') ?? zona;
    return `hora de ${ciudad}`;
}

export const minutos = (t: string) => {
    const [h = 0, m = 0] = t.split(':').map(Number);
    return h * 60 + m;
};

export const HORAS_DEL_DIA: readonly string[] = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 === 0 ? '00' : '30'}`);

/** Lo que falla en un tramo (hasta 3 por día y sin solaparse), o vacío si está bien. */
export function errorDeTramo(tramo: { from: string; to: string }, otros: readonly Tramo[]): string {
    if (minutos(tramo.to) <= minutos(tramo.from)) {
        return 'La hora de fin tiene que ser después de la de inicio.';
    }
    if (otros.some((t) => minutos(tramo.from) < minutos(t.to) && minutos(t.from) < minutos(tramo.to))) {
        return 'Se junta con otro tramo de ese día.';
    }
    return '';
}

// ── Qué puede hacer (listAssistantTools) ──

const MODULOS: Readonly<Record<string, { nombre: string; icono: NombreIcono }>> = {
    clientes: { nombre: 'Identificar al cliente', icono: 'key-round' },
    disponibilidad: { nombre: 'Horario', icono: 'clock' },
    reservas: { nombre: 'Reservas', icono: 'calendar-check' },
    entradas: { nombre: 'Entradas', icono: 'ticket' },
    citas: { nombre: 'Citas', icono: 'calendar-clock' },
};

export interface GrupoHerramientas {
    modulo: string;
    nombre: string;
    icono: NombreIcono;
    disponible: boolean;
    base: boolean;
    herramientas: Herramienta[];
}

/** Por módulo, en el orden del catálogo; las retiradas no salen. «Identificar al cliente» va con el bloque `base` (G592). */
export function gruposDeHerramientas(herramientas: readonly Herramienta[]): GrupoHerramientas[] {
    const grupos = new Map<string, GrupoHerramientas>();
    for (const h of herramientas) {
        if (h.unavailable_reason === 'retired') {
            continue;
        }
        const g = grupos.get(h.module) ?? { modulo: h.module, nombre: MODULOS[h.module]?.nombre ?? h.module, icono: MODULOS[h.module]?.icono ?? 'shield-check', disponible: false, base: false, herramientas: [] };
        g.herramientas.push(h);
        g.disponible ||= h.available;
        g.base ||= h.entitlement === 'base';
        grupos.set(h.module, g);
    }
    return [...grupos.values()];
}

export function lineaDeHerramienta(h: Herramienta, grupo: GrupoHerramientas): string {
    if (!h.available) {
        return `Necesita el bloque ${grupo.nombre}`;
    }
    const efecto = { read: 'Consulta', prepare: 'Deja el pedido pendiente de pago', commit: 'Lo hace por el cliente' }[h.effect];
    if (grupo.base && h.effect === 'prepare') {
        return 'Prepara: no hace nada hasta que el cliente escribe el código';
    }
    return `${efecto}${h.auth_level === 'customer' ? ' · después del código' : ' · sin identificarse'}`;
}

/** El nombre corto que se enseña al negocio (`label` por idioma, G688); `description` es para el modelo. Sin él, el código. */
export const nombreDeHerramienta = (h: Herramienta): string => (h.label ?? '') === '' ? h.code : (h.label ?? h.code);

/** Caducidad del modo humano (`human_mode_expiry_hours`, 1–168 h): «24 horas», «2 días», «7 días». */
export function textoCaducidad(horas: number): string {
    if (horas >= 48 && horas % 24 === 0) {
        return `${String(horas / 24)} días`;
    }
    return horas === 1 ? '1 hora' : `${String(horas)} horas`;
}

/**
 * «Lo has probado después del último cambio» (G688): una prueba del borrador con respuesta que empezó (`TestSession.started_at`)
 * después de la última vez que se guardó (`ConfigVersion.updated_at`). Sin `updated_at` (copia sin guardar) basta la prueba.
 */
export function probadoTrasElCambio(guardado: string | null | undefined, inicioPrueba: string | null): boolean {
    if (inicioPrueba === null) {
        return false;
    }
    return guardado == null || Date.parse(inicioPrueba) >= Date.parse(guardado);
}

export function iconoDeEfecto(h: Herramienta, grupo: GrupoHerramientas): NombreIcono {
    return grupo.base && h.effect === 'prepare' ? 'key-round' : ({ read: 'search', prepare: 'hourglass', commit: 'check-check' } as const)[h.effect];
}

// ── Lo que sabe (11.6) ──

/** «Nueva» o «Cambiada» frente a lo publicado: la misma pregunta (idioma y texto) con otra respuesta es «Cambiada». */
export function chipDePregunta(p: Pregunta, publicadas: readonly Pregunta[] | null): '' | 'Nueva' | 'Cambiada' {
    if (publicadas === null) {
        return '';
    }
    const igual = (a: Pregunta) => a.question === p.question && (a.locale ?? '') === (p.locale ?? '');
    if (publicadas.some((a) => igual(a) && a.answer === p.answer)) {
        return '';
    }
    return publicadas.some(igual) ? 'Cambiada' : 'Nueva';
}

export const TELEFONO_E164 = /^\+[1-9][0-9]{6,14}$/;

export function telefonoLimpio(t: string): string {
    return t.replace(/[\s().-]/g, '');
}

// ── Probar y publicar (11.7) ──

/** Qué cambia del borrador frente a la versión publicada (o, sin ella, qué tendrá la primera). */
export function cambios(pub: ConfiguracionEscrita | null, b: ConfiguracionEscrita, canales: readonly Canal[]): { icono: NombreIcono; texto: string }[] {
    const resumen = (ids: readonly string[]) => resumenCanales(ids, canales).texto.toLocaleLowerCase('es');
    if (pub === null) {
        const n = (b.faqs ?? []).length;
        return [
            { icono: 'bot', texto: `Se llamará ${b.assistant_name === '' ? '…' : b.assistant_name} y hablará en tono ${nombreTono(b.tone).toLocaleLowerCase('es')}` },
            { icono: 'languages', texto: `Idiomas: ${enLista(b.locales.map((l) => nombreIdioma(l).toLocaleLowerCase('es')))}` },
            { icono: 'plug', texto: `Canales: ${resumen(b.channel_ids ?? [])}` },
            { icono: 'message-square-text', texto: `${String(n)} ${n === 1 ? 'pregunta frecuente' : 'preguntas frecuentes'}` },
        ];
    }
    const lista: { icono: NombreIcono; texto: string }[] = [];
    if (pub.assistant_name !== b.assistant_name) {
        lista.push({ icono: 'bot', texto: `Nombre: ${pub.assistant_name} → ${b.assistant_name === '' ? 'sin nombre' : b.assistant_name}` });
    }
    if (pub.tone !== b.tone) {
        lista.push({ icono: 'message-circle', texto: `Tono: ${nombreTono(pub.tone)} → ${nombreTono(b.tone)}` });
    }
    const anade = b.locales.filter((x) => !pub.locales.includes(x));
    const quita = pub.locales.filter((x) => !b.locales.includes(x));
    if (anade.length > 0 || quita.length > 0) {
        const p = [anade.length > 0 ? `añade ${enLista(anade.map((l) => nombreIdioma(l).toLocaleLowerCase('es')))}` : '', quita.length > 0 ? `quita ${enLista(quita.map((l) => nombreIdioma(l).toLocaleLowerCase('es')))}` : ''];
        lista.push({ icono: 'languages', texto: `Idiomas: ${p.filter((x) => x !== '').join(' y ')}` });
    }
    if ([...(pub.channel_ids ?? [])].sort().join() !== [...(b.channel_ids ?? [])].sort().join()) {
        lista.push({ icono: 'plug', texto: `Canales: ${resumen(b.channel_ids ?? [])}` });
    }
    const dA = tramosPorDia(pub.human_hours ?? []);
    const dB = tramosPorDia(b.human_hours ?? []);
    const dias = DIAS.filter((_, i) => textoTramos(dA[i] ?? []) !== textoTramos(dB[i] ?? []));
    if (dias.length > 0) {
        lista.push({ icono: 'headset', texto: `Atención humana: cambia el ${enLista(dias.map((d) => d.toLocaleLowerCase('es')))}` });
    }
    const fPub = pub.faqs ?? [];
    const fB = b.faqs ?? [];
    const nuevas = fB.filter((f) => chipDePregunta(f, fPub) === 'Nueva').length;
    const cambiadas = fB.filter((f) => chipDePregunta(f, fPub) === 'Cambiada').length;
    const quitadas = fPub.filter((f) => !fB.some((g) => g.question === f.question && (g.locale ?? '') === (f.locale ?? ''))).length;
    const fp = [nuevas > 0 ? `${String(nuevas)} ${nuevas === 1 ? 'nueva' : 'nuevas'}` : '', cambiadas > 0 ? `${String(cambiadas)} ${cambiadas === 1 ? 'cambiada' : 'cambiadas'}` : '', quitadas > 0 ? `${String(quitadas)} ${quitadas === 1 ? 'quitada' : 'quitadas'}` : ''].filter((x) => x !== '');
    if (fp.length > 0) {
        lista.push({ icono: 'message-square-text', texto: `Preguntas frecuentes: ${enLista(fp)}` });
    }
    if ((pub.instructions ?? '') !== (b.instructions ?? '')) {
        lista.push({ icono: 'file-text', texto: 'Instrucciones del negocio cambiadas' });
    }
    return lista;
}

/** La versión que ven los clientes, como `ConfigWrite`, para comparar. */
export function comoEscritura(c: Configuracion): ConfiguracionEscrita {
    return escritura(c);
}

export function fechaCorta(iso: string): string {
    const d = new Date(iso);
    return `${d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }).replace('.', '')}, ${d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}`;
}

export function horaCorta(iso: string): string {
    return new Date(iso).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
}

/** El equipo leído con `listMembers`: nombre de cada `member_id` y si la lista está completa (sin más páginas). */
export interface Equipo {
    nombres: ReadonlyMap<string, string>;
    completo: boolean;
}

/**
 * Quién hizo algo (G719): si el `member_id` sigue en el equipo, su nombre de ahora; si no, el guardado al escribir con
 * «(ya no está en el equipo)». Sin poder leer el equipo entero no se sabe si sigue: el guardado, tal cual. null si no hay
 * ni miembro ni nombre.
 */
export function autorDe(id: string | null | undefined, guardado: string | null | undefined, equipo: Equipo | null): string | null {
    const nombre = guardado ?? '';
    if (id != null && equipo !== null) {
        const ahora = equipo.nombres.get(id);
        if (ahora !== undefined) {
            return ahora;
        }
        if (equipo.completo && nombre !== '') {
            return `${nombre} (ya no está en el equipo)`;
        }
    }
    return nombre === '' ? null : nombre;
}

/** Filtros de «Preguntas sin respuesta» (G720). */
export const FILTROS_SIN_RESPUESTA = [
    { valor: 'open', etiqueta: 'Sin responder' },
    { valor: 'answered', etiqueta: 'Respondidas' },
    { valor: 'ignored', etiqueta: 'Ignoradas' },
] as const;

/** «Respondida por Marta · 12 oct» (o sin autor). */
export function lineaDeEstado(status: string, autor: string | null, cuando: string | null | undefined): string {
    const que = status === 'answered' ? 'Respondida' : status === 'ignored' ? 'Ignorada' : 'Abierta de nuevo';
    return [autor === null ? que : `${que} por ${autor}`, cuando == null ? '' : fechaCorta(cuando)].filter((t) => t !== '').join(' · ');
}

/** «Preguntada 4 veces · la última, 28 oct». */
export function lineaDeVeces(veces: number, ultima: string): string {
    return `${veces === 1 ? 'Preguntada 1 vez' : `Preguntada ${veces.toLocaleString('es-ES')} veces`} · la última, ${fechaCorta(ultima)}`;
}
